import * as SQLite from 'wa-sqlite';
import { SCHEMA } from './schema';
import { wrap, type Database } from './sqlite';

/**
 * Открытие базы.
 *
 * **Где живут данные.** В браузере — SQLite поверх OPFS: настоящие файлы в приватном
 * хранилище приложения, тот же способ, что у Android. Это осознанный выбор
 * ([D-44](docs/DECISIONS.md)): схема, миграции и запросы общие с Room, разойтись
 * им негде.
 *
 * **Почему два хранилища, а не одно.** Синхронные ручки OPFS появились в Safari только
 * с iOS 17. На iOS 16 их нет, а в матрице устройств он есть — это нижняя граница
 * для пушей. Поэтому там база ложится в IndexedDB тем же SQLite: медленнее и тяжелее
 * на мегабайт кода, но работает. Выбор делается один раз при открытии и виден в логе.
 *
 * **В тестах** — память: Vitest гоняется в Node, где нет ни OPFS, ни IndexedDB.
 */

export type Storage = 'opfs' | 'indexeddb' | 'memory';

export interface OpenedDatabase extends Database {
  /** Где в итоге легли данные. Показывается в настройках: юрист вправе это знать. */
  storage: Storage;
}

const FILE = 'yatwa.db';

/** Есть ли синхронные ручки OPFS: на них работает быстрый путь. */
async function hasOpfsAccessHandles(): Promise<boolean> {
  try {
    if (!navigator.storage?.getDirectory) return false;
    // Наличие метода в прототипе — единственная надёжная проверка: сам вызов
    // требует воркера, а спрашивать надо до того, как что-то открыто.
    return typeof FileSystemFileHandle.prototype.createSyncAccessHandle === 'function';
  } catch {
    return false;
  }
}

export async function applySchema(db: Database) {
  // Ссылочная целостность в SQLite выключена по умолчанию, а у нас на ней держится
  // удаление дела вместе с документами и событиями. PRAGMA внутри транзакции молча
  // не действует, поэтому она идёт до неё.
  await db.run('PRAGMA foreign_keys = ON');

  /*
   * `synchronous = FULL` — просим SQLite закреплять страницы на каждом коммите.
   *
   * Сама по себе прагма верная, но окно потери она **не закрывает**: проверено
   * 05.09.2026 — запись, сделанная непосредственно перед уходом со страницы, всё
   * равно теряется на ветке IndexedDB. Разбор и что с этим делать — PITFALLS §17.
   */
  await db.run('PRAGMA synchronous = FULL');

  await db.transaction(async (tx) => {
    for (const statement of SCHEMA) await tx.run(statement);
  });
}

/** Открывает базу приложения в браузере. */
export async function openDatabase(): Promise<OpenedDatabase> {
  const opfs = await hasOpfsAccessHandles();

  const factory = opfs
    ? (await import('wa-sqlite/dist/wa-sqlite.mjs')).default
    : (await import('wa-sqlite/dist/wa-sqlite-async.mjs')).default;

  const module = await factory();
  const sqlite3 = SQLite.Factory(module);

  if (opfs) {
    const { AccessHandlePoolVFS } = await import('wa-sqlite/src/examples/AccessHandlePoolVFS.js');
    sqlite3.vfs_register(new AccessHandlePoolVFS('/yatwa'), true);
  } else {
    const { IDBBatchAtomicVFS } = await import('wa-sqlite/src/examples/IDBBatchAtomicVFS.js');

    /*
     * `durability: 'strict'` — не перестраховка, а исправление потери данных.
     *
     * По умолчанию транзакция IndexedDB подтверждается до того, как данные закреплены,
     * и уход со страницы в этот момент их теряет. Ловится так: добавить документ
     * и сразу перейти на другой экран — файл на диске остаётся, а **записи о документе
     * в базе нет**, и уборка предлагает удалить его как осиротевший. Поймано прогоном
     * 05.09.2026; с паузой в три секунды всё сохранялось, без паузы — нет.
     *
     * Строгий режим медленнее на запись, и это правильная плата: юрист снимает документ
     * в суде и убирает телефон в карман, не дожидаясь ничего.
     */
    sqlite3.vfs_register(new IDBBatchAtomicVFS('yatwa', { durability: 'strict' }), true);
  }

  const handle = await sqlite3.open_v2(FILE);
  const db = wrap(sqlite3, handle);
  await applySchema(db);

  return { ...db, storage: opfs ? 'opfs' : 'indexeddb' };
}
